import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Info, RefreshCw } from 'lucide-react';

import { getDomains } from '@/services/api/domains';
import { getCascadeDomains, getCascadeReport } from '@/services/api/cascade-report';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { asArray } from '@/pages/PriceStructure/constants';
import type { DomainRow } from '@/types/cascade-report';

import { LiveStrip } from './components/LiveStrip';
import { MetricLegend } from './components/MetricLegend';
import { MetricTable } from './components/MetricTable';
import { StepTable } from './components/StepTable';
import { SummaryCards } from './components/SummaryCards';
import { PERIODOS } from './format';

interface Domain {
    id: number;
    domain: string;
}

export default function CascadeReport() {
    const [domainId, setDomainId] = useState<number | null>(null);
    // Guarda o nome ao entrar pelo ranking: se a lista de domínios não tiver
    // carregado, o seletor mostraria "Todos" exibindo dados de um só.
    const [domainName, setDomainName] = useState<string | null>(null);
    const [periodo, setPeriodo] = useState(PERIODOS[2]); // últimos 7 dias
    const [block, setBlock] = useState<string | null>(null);
    const [src, setSrc] = useState<string | null>(null);

    const range = periodo.range();
    const filtros = { ...range, domainId, block, src };

    // `asArray`: com o api-rocketads fora do ar, /domains cai no fallback do
    // Vite e devolve HTML com status 200 — sem a guarda, o front branqueia.
    const { data: domainsRaw } = useQuery({ queryKey: ['domains'], queryFn: getDomains });
    const domains = asArray<Domain>(domainsRaw);
    const foraDaLista =
        domainId !== null && !domains.some((d) => d.id === domainId)
            ? { id: domainId, domain: domainName ?? `Domínio ${domainId}` }
            : null;

    const abrirDominio = (id: number | null, nome: string | null = null) => {
        setDomainId(id);
        setDomainName(nome);
        setBlock(null);
    };

    const relatorio = useQuery({
        queryKey: ['cascade-report', filtros],
        queryFn: () => getCascadeReport(filtros),
    });

    const rede = useQuery({
        queryKey: ['cascade-domains', range],
        queryFn: () => getCascadeDomains(range),
        enabled: domainId === null,
    });

    const dados = relatorio.data;
    // Olha `attempts`, não `cascades`: solicitação é o fato bruto do evento,
    // enquanto `cascades` é um campo derivado que pode faltar em rollup antigo.
    // Amarrar o vazio ao derivado escondia a tela inteira tendo dado.
    const semDado = !relatorio.isLoading && (dados?.totals.attempts ?? 0) === 0;
    // Contagem ausente ou parcial: com parte do período consolidada antes do
    // contador, o numerador vem inteiro e o denominador pela metade — e o
    // preenchimento passaria de 100%.
    const semCascatas =
        !!dados && dados.totals.attempts > 0 && !dados.totals.cascades_reliable;

    const blocos = dados?.by_block.map((b) => b.block) ?? [];

    return (
        <div className="space-y-5 p-6">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        Relatório de Cascata
                    </h1>
                    <p className="text-sm text-gray-500">
                        O que aconteceu na hora da compra: qual step vendeu, quanto custou e o que
                        nunca comprou.
                    </p>
                </div>
                <button
                    onClick={() => {
                        relatorio.refetch();
                        rede.refetch();
                    }}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                >
                    <RefreshCw
                        className={`h-3.5 w-3.5 ${relatorio.isFetching ? 'animate-spin' : ''}`}
                    />
                    Atualizar
                </button>
            </header>

            {/* O agora fica no topo: é a referência para tudo que vem abaixo,
                que é consolidado e olha para trás. */}
            <LiveStrip domainId={domainId} />

            {/* ---------- filtros ---------- */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
                <select
                    value={domainId ?? ''}
                    onChange={(e) =>
                        abrirDominio(e.target.value ? Number(e.target.value) : null)
                    }
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                >
                    <option value="">Todos os domínios</option>
                    {foraDaLista && (
                        <option value={foraDaLista.id}>{foraDaLista.domain}</option>
                    )}
                    {domains.map((d) => (
                        <option key={d.id} value={d.id}>
                            {d.domain}
                        </option>
                    ))}
                </select>

                <div className="flex rounded-lg border border-gray-200">
                    {PERIODOS.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setPeriodo(p)}
                            className={`px-3 py-1.5 text-sm first:rounded-l-lg last:rounded-r-lg ${
                                periodo.id === p.id
                                    ? 'bg-blue-50 font-medium text-blue-700'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                <select
                    value={block ?? ''}
                    onChange={(e) => setBlock(e.target.value || null)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                >
                    <option value="">Todos os blocos</option>
                    {blocos.map((b) => (
                        <option key={b} value={b}>
                            {b}
                        </option>
                    ))}
                </select>

                <select
                    value={src ?? ''}
                    onChange={(e) => setSrc(e.target.value || null)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                >
                    <option value="">Todas as origens</option>
                    <option value="structure">Estrutura automática</option>
                    <option value="manual">Granulação manual</option>
                    <option value="default">Steps fixos</option>
                </select>
            </div>

            {relatorio.isError && (
                <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    Não foi possível carregar o relatório.
                </p>
            )}

            {relatorio.isLoading && (
                <p className="py-12 text-center text-sm text-gray-500">Carregando…</p>
            )}

            {semDado && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
                    <p className="text-sm font-medium text-gray-700">
                        Nenhuma cascata registrada neste recorte.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        O relatório lê a consolidação diária, que roda de madrugada. Eventos de hoje
                        só aparecem depois dela.
                    </p>
                </div>
            )}

            {semCascatas && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    A contagem de cascatas deste período está incompleta — parte dele foi
                    consolidada antes de o contador existir, então o preenchimento passaria de
                    100%. Ele e a receita por mil cascatas aparecem como “—” até você reprocessar
                    o rollup dos dias do período. As demais métricas estão corretas.
                </div>
            )}

            {dados && !semDado && (
                <>
                    <SummaryCards totals={dados.totals} calibration={dados.calibration} />
                    <MetricLegend />

                    {domainId === null ? (
                        <Tabs defaultValue="dominios">
                            <TabsList>
                                <TabsTrigger value="dominios">Ranking de domínios</TabsTrigger>
                                <TabsTrigger value="steps">Por step</TabsTrigger>
                            </TabsList>

                            <TabsContent value="dominios" className="mt-3">
                                <MetricTable<DomainRow>
                                    rows={rede.data?.domains ?? []}
                                    label="Domínio"
                                    name={(d) => d.domain}
                                    onRowClick={(d) => abrirDominio(d.domain_id, d.domain)}
                                    empty="Nenhum domínio com cascata no período."
                                />
                                <p className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                                    <Info className="h-3 w-3" />
                                    Clique numa linha para abrir o domínio.
                                </p>
                            </TabsContent>

                            <TabsContent value="steps" className="mt-3">
                                <StepTable steps={dados.by_step} />
                            </TabsContent>
                        </Tabs>
                    ) : (
                        <Tabs defaultValue="steps">
                            <TabsList>
                                <TabsTrigger value="steps">Por step</TabsTrigger>
                                <TabsTrigger value="blocos">Por bloco</TabsTrigger>
                                <TabsTrigger value="paises">Por país</TabsTrigger>
                            </TabsList>

                            <TabsContent value="steps" className="mt-3">
                                <StepTable steps={dados.by_step} />
                            </TabsContent>

                            <TabsContent value="blocos" className="mt-3">
                                <MetricTable
                                    rows={dados.by_block}
                                    label="Bloco"
                                    name={(b) => b.block}
                                />
                            </TabsContent>

                            <TabsContent value="paises" className="mt-3">
                                <MetricTable
                                    rows={dados.by_country}
                                    label="País"
                                    name={(c) => c.country}
                                />
                            </TabsContent>
                        </Tabs>
                    )}
                </>
            )}
        </div>
    );
}
